import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import Sparkline from '@/components/common/Sparkline';
import { ThumbsUp, Users, Clock, AlertCircle } from 'lucide-react';

type TimeRange = '24h' | '7d';

type KpiState = {
  bookingRate: number;       // %
  transferRate: number;      // %
  timeToBookSec: number;     // seconds
  objectionsAvg: number;     // count
  toolLatencyMs: number;     // ms
};

type Trends = {
  bookingRate: number[];
  transferRate: number[];
  timeToBook: number[];
  objections: number[];
  toolLatency: number[];
};

const defaultKpis: KpiState = {
  bookingRate: 0,
  transferRate: 0,
  timeToBookSec: 0,
  objectionsAvg: 0,
  toolLatencyMs: 0,
};

const defaultTrends: Trends = {
  bookingRate: [],
  transferRate: [],
  timeToBook: [],
  objections: [],
  toolLatency: [],
};

function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function AIAgentKpiWidget() {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [kpis, setKpis] = useState<KpiState>(defaultKpis);
  const [trends, setTrends] = useState<Trends>(defaultTrends);

  // Live refresh on DB changes
  useEffect(() => {
    const ch = supabase
      .channel('ai_kpi_widget')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_voice_calls' }, () => load())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ai_call_metrics' }, () => load());

    ch.subscribe();
    return () => {
      try { supabase.removeChannel(ch); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange]);

  useEffect(() => { load(); }, [timeRange]);

  async function load() {
    try {
      setLoading(true);

      const now = new Date();
      const start = new Date();
      if (timeRange === '24h') start.setHours(now.getHours() - 24);
      if (timeRange === '7d') start.setDate(now.getDate() - 7);

      // Fetch recent AI calls
      const callsQ = supabase
        .from('ai_voice_calls')
        .select('created_at, status, transferred_to_agent, duration')
        .gte('created_at', start.toISOString())
        .order('created_at', { ascending: false })
        .limit(500);
      const { data: calls, error: callsErr } = await callsQ;
      if (callsErr) throw callsErr;

      // Fetch AI metrics
      const metricsQ = supabase
        .from('ai_call_metrics')
        .select('metric_type, metric_value, created_at')
        .gte('created_at', start.toISOString())
        .order('created_at', { ascending: false })
        .limit(2000);
      const { data: metrics, error: mErr } = await metricsQ;
      if (mErr) throw mErr;

      // Aggregate KPIs
      const avg = (arr: number[]) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
      const m = (t: string) => (metrics || []).filter((x: any) => x.metric_type === t).map((x: any) => Number(x.metric_value) || 0);

      const bookingRateFromMetrics = avg(m('booking_rate')); // already 0-100
      const timeToBookSec = avg(m('time_to_book_seconds'));
      const objectionsAvg = avg(m('objection_count'));
      const toolLatencyMs = avg(m('tool_latency_ms'));

      const totalCalls = (calls || []).length;
      const transferred = (calls || []).filter((c: any) => !!c.transferred_to_agent).length;
      const transferRate = totalCalls ? (transferred / totalCalls) * 100 : 0;

      // Fallback for booking rate if metrics missing
      const bookingRate = Number.isFinite(bookingRateFromMetrics) && bookingRateFromMetrics > 0
        ? bookingRateFromMetrics
        : 0;

      setKpis({
        bookingRate: Math.round(bookingRate),
        transferRate: Math.round(transferRate),
        timeToBookSec: timeToBookSec,
        objectionsAvg,
        toolLatencyMs,
      });

      // Trends bucketing
      const endMs = now.getTime();
      const startMs = start.getTime();
      const hour = 60 * 60 * 1000;
      const day = 24 * hour;
      const bucket = timeRange === '24h' ? hour : day;
      const n = Math.max(2, Math.ceil((endMs - startMs) / bucket));
      const idx = (tsMs: number) => Math.min(Math.max(Math.floor((tsMs - startMs) / bucket), 0), n - 1);

      const mk = (len: number) => Array.from({ length: len }, () => 0);
      const cnt = (len: number) => Array.from({ length: len }, () => 0);

      const br = mk(n), brN = cnt(n);
      const ttb = mk(n), ttbN = cnt(n);
      const obj = mk(n), objN = cnt(n);
      const lat = mk(n), latN = cnt(n);
      const trNum = mk(n), trDen = cnt(n);

      (metrics || []).forEach((row: any) => {
        const i = idx(new Date(row.created_at).getTime());
        const v = Number(row.metric_value) || 0;
        switch (row.metric_type) {
          case 'booking_rate': br[i] += v; brN[i]++; break;
          case 'time_to_book_seconds': ttb[i] += v; ttbN[i]++; break;
          case 'objection_count': obj[i] += v; objN[i]++; break;
          case 'tool_latency_ms': lat[i] += v; latN[i]++; break;
        }
      });

      (calls || []).forEach((c: any) => {
        const i = idx(new Date(c.created_at).getTime());
        trDen[i]++; if (c.transferred_to_agent) trNum[i]++;
      });

      const safeAvg = (s: number, n2: number) => (n2 > 0 ? s / n2 : 0);
      setTrends({
        bookingRate: br.map((s, i) => Math.round(safeAvg(s, brN[i]))),
        timeToBook: ttb.map((s, i) => safeAvg(s, ttbN[i])),
        objections: obj.map((s, i) => safeAvg(s, objN[i])),
        toolLatency: lat.map((s, i) => safeAvg(s, latN[i])),
        transferRate: trNum.map((s, i) => (trDen[i] > 0 ? Math.round((s / trDen[i]) * 100) : 0)),
      });

    } catch (e) {
      console.error('[AI KPI Widget] load error', e);
    } finally {
      setLoading(false);
    }
  }

  const header = useMemo(() => (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        <span className="text-base font-semibold text-white">AI Agents</span>
        <span className="text-[10px] text-slate-400">Live</span>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setTimeRange('24h')}
          className={`text-xs px-2 py-1 rounded ${timeRange === '24h' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300'}`}
        >
          24h
        </button>
        <button
          onClick={() => setTimeRange('7d')}
          className={`text-xs px-2 py-1 rounded ${timeRange === '7d' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300'}`}
        >
          7d
        </button>
      </div>
    </div>
  ), [timeRange]);

  return (
    <div className="p-3 rounded-lg border border-slate-800 bg-slate-900">
      {header}
      {loading ? (
        <div className="h-20 flex items-center justify-center">
          <div className="animate-spin h-5 w-5 rounded-full border-b-2 border-blue-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-3 rounded bg-slate-950 border border-slate-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-400">Booking Rate</span>
              <ThumbsUp size={14} className="text-emerald-400" />
            </div>
            <div className="text-xl font-bold text-white">{Math.round(kpis.bookingRate)}%</div>
            <div className="mt-1"><Sparkline data={trends.bookingRate} height={28} stroke="#10b981" /></div>
          </div>

          <div className="p-3 rounded bg-slate-950 border border-slate-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-400">Transfer Rate</span>
              <Users size={14} className="text-amber-400" />
            </div>
            <div className="text-xl font-bold text-white">{Math.round(kpis.transferRate)}%</div>
            <div className="mt-1"><Sparkline data={trends.transferRate} height={28} stroke="#f59e0b" /></div>
          </div>

          <div className="p-3 rounded bg-slate-950 border border-slate-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-400">Time to Book</span>
              <Clock size={14} className="text-indigo-400" />
            </div>
            <div className="text-xl font-bold text-white">{formatDuration(kpis.timeToBookSec)}</div>
            <div className="mt-1"><Sparkline data={trends.timeToBook} height={28} stroke="#6366f1" /></div>
          </div>

          <div className="p-3 rounded bg-slate-950 border border-slate-800">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-400">Objections • Tool Latency</span>
              <AlertCircle size={14} className="text-red-400" />
            </div>
            <div className="text-sm text-slate-300">
              {kpis.objectionsAvg.toFixed(2)} avg • {Math.round(kpis.toolLatencyMs)} ms
            </div>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <Sparkline data={trends.objections} height={28} stroke="#f59e0b" />
              <Sparkline data={trends.toolLatency} height={28} stroke="#10b981" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
